import ExcelJS from 'exceljs';
import { formatDate } from 'lib/functions/utils';

const DATE_FORMAT = 'dd-mm-yyyy';

export interface BookExcelLine {
  date: string;
  accountName?: string;
  account?: string;
  businessIdentifier?: string;
  businessName?: string;
  description?: string;
  entryId?: number;
  category?: string;
  className?: string;
  documentId?: string;
  debit?: number;
  credit?: number;
}

export interface MajorBookExcelLine extends BookExcelLine {
  account?: string;
  accountName?: string;
  debitFinal?: number;
  creditFinal?: number;
  [key: string]: unknown;
}

export interface AccountBookExcelLine extends BookExcelLine {
  debitFinal?: number;
  creditFinal?: number;
  accountName?: string;
  account?: string;
}

interface MajorBookLine extends BookExcelLine {
  businessIdentifier: string;
  businessName: string;
  account?: string;
  accountName?: string;
  debitFinal?: number;
  creditFinal?: number;
  [key: string]: unknown;
}

interface MajorBookCategory {
  groupedLines: MajorBookLine[];
}

interface MajorBookResponse {
  majorBookAccounts: MajorBookCategory[];
  groupedByCategoryAndBusiness?: Record<string, unknown>;
}

interface ExportOptions {
  startDate?: Date;
  endDate?: Date;
  businessIdentifier?: string;
  businessName?: string;
  accountCode?: string;
  accountName?: string;
  balanceType?: string;
  businessAccountIdentifier?: string;
  businessAccountName?: string;
  includeAccountInfo?: boolean;
}

const BASE_HEADERS = [
  'Fecha',
  'RUT',
  'Nombre empresa',
  'Descripción',
  'ID Asiento',
  'Clase Asiento',
  'Documento',
  'Debe',
  'Haber',
] as const;

function getHeaders(includeAccountInfo = true): readonly string[] {
  if (!includeAccountInfo) {
    return [
      'Fecha',
      'RUT',
      'Nombre empresa',
      'Nombre Cuenta',
      'Codigo Cuenta',
      'Descripción',
      'ID Asiento',
      'Clase Asiento',
      'Documento',
      'Debe',
      'Haber',
    ] as const;
  }

  return BASE_HEADERS;
}

type CellValue = string | number;

const STYLES = {
  header: {
    font: { bold: true },
    alignment: { vertical: 'middle', horizontal: 'center' } as const,
    fill: {
      type: 'pattern' as const,
      pattern: 'solid' as const,
      fgColor: { argb: 'FFDDDDDD' },
    },
  },
  columnWidth: 20,
  numberFormat: '#,##0',
} as const;

function lineToRow(
  line: BookExcelLine,
  includeAccountInfo = true,
): CellValue[] {
  const baseRow: CellValue[] = [
    formatDate(new Date(line.date), DATE_FORMAT) || '',
    line.businessIdentifier || '',
    line.businessName || '',
    line.description || '',
    Number(line.entryId) ?? '',
    getClassDisplay(line),
    line.documentId || '',
    line.debit ?? 0,
    line.credit ?? 0,
  ];

  if (!includeAccountInfo) {
    baseRow.splice(3, 0, line.accountName || '', line.account || '');
  }

  return baseRow;
}

function getClassDisplay(line: {
  category?: string | null;
  className?: string | null;
}): string {
  return (line.category ?? line.className) || '';
}

function calculateTotalsFromLines(lines: BookExcelLine[]): {
  totalDebit: number;
  totalCredit: number;
} {
  return lines.reduce(
    (acc, line) => ({
      totalDebit: acc.totalDebit + (line.debit ?? 0),
      totalCredit: acc.totalCredit + (line.credit ?? 0),
    }),
    { totalDebit: 0, totalCredit: 0 },
  );
}

function createReportInfoRows(params: {
  businessName?: string;
  businessIdentifier?: string;
  period: string;
  typeLabel: string;
  typeValue: string;
  extraRows?: (string | undefined)[][];
}): (string | undefined)[][] {
  const {
    businessName,
    businessIdentifier,
    period,
    typeLabel,
    typeValue,
    extraRows = [],
  } = params;
  return [
    ['Informacion de la consulta', undefined],
    ['Razón social:', businessName || 'N/A'],
    ['RUT:', businessIdentifier || 'N/A'],
    [typeLabel, typeValue],
    ['Periodo:', period],
    ...extraRows,
    ['Fecha de consulta:', formatDate(new Date(), DATE_FORMAT)],
    [],
  ];
}

const ReportInfoMapper = {
  create(options: ExportOptions): (string | undefined)[][] {
    const {
      startDate,
      endDate,
      businessName,
      businessIdentifier,
      accountCode,
    } = options;
    const period =
      startDate && endDate
        ? `${formatDate(startDate, DATE_FORMAT)} a ${formatDate(endDate, DATE_FORMAT)}`
        : '';
    return createReportInfoRows({
      businessName,
      businessIdentifier,
      period,
      typeLabel: 'Tipo de reporte:',
      typeValue: 'Libro Mayor',
    });
  },
};

class ExcelWorksheetBuilder {
  protected worksheet: ExcelJS.Worksheet;

  constructor(worksheet: ExcelJS.Worksheet) {
    this.worksheet = worksheet;
  }

  addInfoSection(rows: (string | undefined)[][]): this {
    rows.forEach((rowData) => {
      const row = this.worksheet.addRow(rowData);
      if (rowData.length > 0) {
        row.getCell(1).font = { bold: true };
      }
    });
    return this;
  }

  addHeaderRow(headers: readonly string[]): this {
    const row = this.worksheet.addRow([...headers]);
    this.applyHeaderStyle(row);

    const debitColumn = headers.length - 1;
    const creditColumn = headers.length;
    this.worksheet.getColumn(debitColumn).numFmt = STYLES.numberFormat;
    this.worksheet.getColumn(creditColumn).numFmt = STYLES.numberFormat;
    return this;
  }

  addDataRows(rows: CellValue[][]): this {
    rows.forEach((rowData) => this.worksheet.addRow(rowData));
    return this;
  }

  addTotalsRow(
    totalDebit: number,
    totalCredit: number,
    headers: readonly string[],
  ): this {
    const totalsRowData: CellValue[] = Array(headers.length).fill('');
    totalsRowData[0] = 'Totales:';
    totalsRowData[headers.length - 2] = totalDebit;
    totalsRowData[headers.length - 1] = totalCredit;
    const totalsRow = this.worksheet.addRow(totalsRowData);

    totalsRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE6E6E6' },
      };
      cell.border = {
        top: { style: 'double' },
        bottom: { style: 'thin' },
      };
    });

    return this;
  }

  protected applyHeaderStyle(row: ExcelJS.Row): void {
    row.eachCell((cell) => {
      cell.font = STYLES.header.font;
      cell.alignment = STYLES.header.alignment;
      cell.fill = STYLES.header.fill;
      this.worksheet.getColumn(cell.col).width = STYLES.columnWidth;
    });
  }
}

class ExcelDocumentBuilder {
  private workbook: ExcelJS.Workbook;
  private worksheetBuilder: ExcelWorksheetBuilder | null = null;

  constructor() {
    this.workbook = new ExcelJS.Workbook();
  }

  createWorksheet(name: string): ExcelWorksheetBuilder {
    const worksheet = this.workbook.addWorksheet(name);
    this.worksheetBuilder = new ExcelWorksheetBuilder(worksheet);
    return this.worksheetBuilder;
  }

  async toArrayBuffer(): Promise<ArrayBuffer> {
    const buffer = await this.workbook.xlsx.writeBuffer();
    return buffer as ArrayBuffer;
  }
}

const FileDownloader = {
  async downloadExcel(data: ArrayBuffer, fileName: string): Promise<void> {
    const blob = new Blob([data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.click();

    URL.revokeObjectURL(url);
  },
};

function getExportParams(
  options: ExportOptions,
  isFullBook: boolean,
): {
  infoRows: (string | undefined)[][];
  worksheetName: string;
  fileName: string;
} {
  const balanceType = options.balanceType || 'Libro';

  return {
    infoRows: isFullBook
      ? ReportInfoMapper.create(options)
      : AccountReportInfoMapper.create(options),
    worksheetName: balanceType,
    fileName: `${balanceType}_${formatDate(new Date(), DATE_FORMAT)}.xlsx`,
  };
}

async function buildAndDownloadBook(params: {
  lines: BookExcelLine[];
  totalDebit: number;
  totalCredit: number;
  options: ExportOptions;
  isFullBook: boolean;
}): Promise<void> {
  const { lines, totalDebit, totalCredit, options, isFullBook } = params;
  const { infoRows, worksheetName, fileName } = getExportParams(
    options,
    isFullBook,
  );
  const includeAccountInfo = options.includeAccountInfo ?? true;
  const headers = getHeaders(includeAccountInfo);
  const dataRows = lines.map((line) => lineToRow(line, includeAccountInfo));

  const document = new ExcelDocumentBuilder();
  document
    .createWorksheet(worksheetName)
    .addInfoSection(infoRows)
    .addHeaderRow(headers)
    .addDataRows(dataRows)
    .addTotalsRow(totalDebit, totalCredit, headers);

  const buffer = await document.toArrayBuffer();
  await FileDownloader.downloadExcel(buffer, fileName);
}

export const exportMajorBookToExcel = async (
  entries: MajorBookResponse,
  options: ExportOptions,
): Promise<void> => {
  if (!entries?.majorBookAccounts) return;

  const lines: BookExcelLine[] = entries.majorBookAccounts.flatMap(
    (category) => category.groupedLines,
  );

  await exportBookToExcel(lines, options, true);
};

const AccountReportInfoMapper = {
  create(options: ExportOptions): (string | undefined)[][] {
    const {
      startDate,
      endDate,
      businessName,
      businessIdentifier,
      accountCode,
      accountName,
      balanceType,
      businessAccountIdentifier,
      businessAccountName,
      includeAccountInfo = true,
    } = options;

    const period =
      startDate && endDate
        ? `${formatDate(startDate, DATE_FORMAT)} a ${formatDate(endDate, DATE_FORMAT)}`
        : 'N/A';

    const isLibroAuxiliar = balanceType === 'Libro Auxiliar';
    const accountRows: (string | undefined)[][] = includeAccountInfo
      ? isLibroAuxiliar
        ? [
            ['Nombre Empresa:', businessAccountName ?? 'N/A'],
            ['RUT:', businessAccountIdentifier ?? 'N/A'],
          ]
        : [
            ['Cuenta:', accountCode ?? 'N/A'],
            ['Nombre de Cuenta:', accountName ?? 'N/A'],
          ]
      : [];

    return [
      ['Informacion de la consulta:', undefined],
      ['Razón social:', businessName || 'N/A'],
      ['RUT:', businessIdentifier || 'N/A'],
      ['Tipo de balance:', balanceType],
      ['Periodo:', period],
      ...accountRows,
      ['Fecha de consulta:', formatDate(new Date(), DATE_FORMAT)],
      [],
    ];
  },
};

export const exportBookToExcel = async (
  lines: BookExcelLine[],
  options: ExportOptions,
  isFullBook = false,
): Promise<void> => {
  if (!lines || lines.length === 0) return;

  const totals = calculateTotalsFromLines(lines);

  await buildAndDownloadBook({
    lines,
    totalDebit: totals.totalDebit,
    totalCredit: totals.totalCredit,
    options,
    isFullBook,
  });
};
